import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button, Tabs, Card, Space, Spin, Alert } from '../../components';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { usePreflightMonitor } from '../../components/PreflightMonitor/hooks';
import { PreflightComponent } from '../../components/PreflightMonitor/components';
import { SourceInventoryTable } from '../../components/SourceInventoryTable/SourceInventoryTable';
import {
    formatCSVValue,
    csvFileGenerator,
    makeNiceName,
    getOrganizationData,
    getSessionVar,
    getDiskDisplayInfo,
    getMountDisplayInfo,
} from '../../lib/helpers';
import { generatePreflightsPDF, generateInventoryPDF } from '../../lib/pdfHelpers';
import { useNotificationContext } from '../../providers/NotificationProvider';
import './SourceInspectionPage.scss';

/**
 * Source Inspection Page Component
 * Displays preflight information and inventory details for inspected sources
 * Based on Backbone source_inspection_module.js
 */
export const SourceInspectionPage = () => {
    const [activeTab, setActiveTab] = useState('preflight');
    const { notifySuccess, notifyError } = useNotificationContext();

    // Get data from sessionStorage (set during navigation from source inspection dialog)
    const sessionData = useMemo(() => {
        try {
            const selectedModels = getSessionVar('selectedModels') || [];
            const preflightPayload = getSessionVar('preflight_payload') || [];
            const inspectionContext = getSessionVar('sourceInspectionContext') || {};

            return {
                selectedModels,
                preflightPayload,
                inspectionContext,
            };
        } catch (error) {
            console.error('Error parsing session data:', error);
            return {
                selectedModels: [],
                preflightPayload: [],
                inspectionContext: {},
            };
        }
    }, []);

    // Memoized callback for preflight completion
    const handlePreflightComplete = useCallback((result) => {
        console.log('Preflight monitoring complete:', result);
    }, []);

    // Use preflight monitor hook directly (not as modal)
    const {
        preflightComponents,
        expandedComponents,
        selectedRowKeys,
        nonErrorsHiddenPerComponent,
        isLoading: preflightLoading,
        allComplete,
        canContinue,
        error: preflightError,
        setExpandedComponents,
        setSelectedRowKeys,
        setNonErrorsHiddenPerComponent,
        handleErrorWarningRecovery,
        getRecoveryState,
        handleRestart,
    } = usePreflightMonitor({
        preflightRequests: sessionData.preflightPayload,
        migrationContext: sessionData.inspectionContext,
        open: sessionData.preflightPayload.length > 0,
        inspectSourcesMode: true,
        onComplete: handlePreflightComplete,
    });

    // Auto-expand components with errors on initial load
    useEffect(() => {
        if (preflightComponents.length > 0 && !preflightLoading) {
            const componentsWithErrors = preflightComponents
                .filter((component) => component.type !== 'target_cloud')
                .filter((component) => {
                    // Check if component has errors or warnings
                    if (component.state === 'error') return true;
                    if (component.state === 'success' && component.errorType === 'warning') return true;
                    if (component.errors && component.errors.length > 0) return true;
                    return false;
                })
                .map((component) => component.resource_id);

            if (componentsWithErrors.length > 0) {
                setExpandedComponents((prev) => {
                    const newSet = new Set(prev);
                    componentsWithErrors.forEach((resourceId) => newSet.add(resourceId));
                    return newSet;
                });
            }
        }
    }, [preflightComponents, preflightLoading, setExpandedComponents]);

    // Download handlers following Backbone implementation
    const handleDownloadPreflightsPDF = useCallback(async () => {
        if (!sessionData.selectedModels.length) {
            notifyError({ description: 'No sources found for PDF generation' });
            return;
        }

        try {
            notifySuccess({ description: 'Generating PDF report...' });

            // Get organization data for project name
            const orgData = getOrganizationData();
            const projectName = orgData.name || 'Source Inspection';

            await generatePreflightsPDF(preflightComponents, sessionData.selectedModels, projectName);
        } catch (error) {
            console.error('Error generating preflight PDF:', error);
            notifyError({ description: 'Failed to generate PDF file' });
        }
    }, [sessionData.selectedModels, preflightComponents, notifySuccess, notifyError]);

    const handleDownloadPreflightsCSV = useCallback(() => {
        if (!preflightComponents.length) {
            notifyError({ description: 'No preflight data available for CSV export' });
            return;
        }

        try {
            notifySuccess({ description: 'Your Migration Report is being generated, the download will start shortly' });

            const dataCsv = [];
            const isVMBased = sessionData.selectedModels[0]?.collection_type === 'vm';
            const vmNameOrIpAddress = isVMBased ? 'VM Name' : 'IP Address';
            const categoriesCSV = [vmNameOrIpAddress, 'Status', 'Preflight Check', 'Description', 'Errors/Warnings'];
            dataCsv.push(categoriesCSV);

            // Process preflight components data
            preflightComponents.forEach((component) => {
                if (component.type !== 'target_cloud') {
                    // Status text based on errorType (same logic as PDF)
                    const stateDisplayedValue = component.errorType
                        ? component.errorType.toLowerCase() === 'fatal'
                            ? 'Error'
                            : makeNiceName(component.errorType)
                        : component.state === 'success'
                          ? 'Ready'
                          : makeNiceName(component.state);
                    const rowCsv = [];

                    // Find matching source model for reliable identification
                    const matchedModel = sessionData.selectedModels.find((m) => m.id === component.resource_id);
                    const firstColValue = isVMBased
                        ? matchedModel?.vm_name ||
                          component.sourceData?.name ||
                          matchedModel?.host ||
                          component.host ||
                          component.hostName ||
                          'Unknown'
                        : matchedModel?.host ||
                          component.host ||
                          component.hostName ||
                          component.sourceData?.host ||
                          'Unknown';

                    rowCsv.push(formatCSVValue(firstColValue));

                    // Extract errors from individual preflight checks
                    let hasErrors = false;
                    if (component.data?.content && Array.isArray(component.data.content)) {
                        component.data.content.forEach((check) => {
                            if (check.errors && check.errors.length > 0) {
                                hasErrors = true;
                                // Group all errors from the same check into one row with line breaks
                                const errorMessages = check.errors.map((error) => error.message || '').join('\n');
                                const currentRow = [...rowCsv];
                                currentRow.push(formatCSVValue(stateDisplayedValue));
                                currentRow.push(formatCSVValue(check.label || check.name || 'Unknown Check'));
                                currentRow.push(formatCSVValue(check.description || ''));
                                currentRow.push(formatCSVValue(errorMessages));
                                dataCsv.push(currentRow);
                            }
                        });
                    }

                    // If no individual check errors but component has errors, use component-level errors
                    if (!hasErrors && component.errors && component.errors.length) {
                        // Group all component-level errors into one row with line breaks
                        const errorMessages = component.errors.map((error) => error.message || '').join('\n');
                        const errorNames = component.errors.map((error) => error.name || 'Unknown Check').join('\n');
                        const errorDescriptions = component.errors.map((error) => error.description || '').join('\n');

                        const currentRow = [...rowCsv];
                        currentRow.push(formatCSVValue(stateDisplayedValue));
                        currentRow.push(formatCSVValue(errorNames));
                        currentRow.push(formatCSVValue(errorDescriptions));
                        currentRow.push(formatCSVValue(errorMessages));
                        dataCsv.push(currentRow);
                        hasErrors = true;
                    }

                    // If no errors at all - add just status
                    if (!hasErrors) {
                        rowCsv.push(formatCSVValue(stateDisplayedValue));
                        rowCsv.push(formatCSVValue(''));
                        rowCsv.push(formatCSVValue(''));
                        rowCsv.push(formatCSVValue(''));
                        dataCsv.push(rowCsv);
                    }
                }
            });

            csvFileGenerator(dataCsv, 'Source Inspection - Preflight Information');
        } catch (error) {
            console.error('Error generating preflight CSV:', error);
            notifyError({ description: 'Failed to generate CSV file' });
        }
    }, [preflightComponents, sessionData.selectedModels, notifySuccess, notifyError]);

    const handleDownloadInventoryPDF = useCallback(async () => {
        if (!sessionData.selectedModels.length) {
            notifyError({ description: 'No sources found for PDF generation' });
            return;
        }

        try {
            notifySuccess({ description: 'Generating PDF report...' });

            // Get organization data for project name
            const orgData = getOrganizationData();
            const projectName = orgData.name || 'Source Inspection';

            await generateInventoryPDF(sessionData.selectedModels, projectName);
        } catch (error) {
            console.error('Error generating inventory PDF:', error);
            notifyError({ description: 'Failed to generate PDF file' });
        }
    }, [sessionData.selectedModels, notifySuccess, notifyError]);

    const handleDownloadInventoryCSV = useCallback(() => {
        if (!sessionData.selectedModels.length) {
            notifyError({ description: 'No sources found for CSV export' });
            return;
        }

        try {
            notifySuccess({ description: 'Your Migration Report is being generated, the download will start shortly' });

            const dataCsv = [];
            const isVMBased = sessionData.selectedModels[0]?.collection_type === 'vm';
            const vmNameOrIpAddress = isVMBased ? 'VM Name' : 'IP Address';
            const categoriesCsv = [
                vmNameOrIpAddress,
                'Hostname',
                'Operating System Version',
                'CPU',
                'Memory',
                'Disks',
                'Recommended CPU',
                'Recommended RAM',
            ];

            if (!isVMBased) {
                categoriesCsv.push('Software');
            }
            dataCsv.push(categoriesCsv);

            let headerAdjusted = false; // Track if we've already adjusted header for missing recommended config

            // Process each source's inventory data
            sessionData.selectedModels.forEach((src) => {
                const rowCsv = [];
                // First column should match header intent: VM Name for VM-based, IP Address for OS-based
                // Always include IP/hostname even for sources with errors
                const firstColValue = isVMBased
                    ? src.vm_name || src.attributes?.network?.hostname || src.host || 'Unknown'
                    : src.host || src.attributes?.network?.ipaddress || 'Unknown';
                rowCsv.push(formatCSVValue(firstColValue));

                // If inspection failed, show basic info but mark other fields as unavailable
                if (!src.attributes || src.attributes.state !== 'success') {
                    rowCsv.push(formatCSVValue(src.hostname || 'N/A')); // Hostname
                    rowCsv.push(formatCSVValue('Inspection Failed')); // OS
                    rowCsv.push(formatCSVValue('N/A')); // CPU
                    rowCsv.push(formatCSVValue('N/A')); // Memory
                    rowCsv.push(formatCSVValue('N/A')); // Disks
                    rowCsv.push(formatCSVValue('')); // Recommended CPU
                    rowCsv.push(formatCSVValue('')); // Recommended RAM
                    if (!isVMBased) {
                        rowCsv.push(formatCSVValue('N/A')); // Software
                    }
                    dataCsv.push(rowCsv);
                    return;
                }

                rowCsv.push(formatCSVValue(src.attributes.network?.hostname || 'N/A'));
                rowCsv.push(formatCSVValue(src.attributes.os?.desc || 'N/A'));

                let cpuCsv = '';
                let mountsCsv = '';

                if (isVMBased) {
                    // VM-based sources - use processed CPU value
                    cpuCsv = src.attributes.cpu?.processors?.length?.toString() || '0';

                    if (src.attributes.storage?.vm_disks) {
                        // Use getDiskDisplayInfo to filter disks (vm_disks is an object, not array)
                        const processedDisks = getDiskDisplayInfo(src.attributes.storage.vm_disks);
                        processedDisks.forEach((disk) => {
                            mountsCsv += `${disk.disk_label}, Size ${disk.size} GiB, ${disk.disk_provisioning_type}\n`;
                        });
                    }
                } else {
                    // OS-based sources - list processor models
                    if (src.attributes.cpu?.processors && Array.isArray(src.attributes.cpu.processors)) {
                        src.attributes.cpu.processors.forEach((cpu) => {
                            cpuCsv += `${cpu.name || cpu.model || 'Unknown'}\n`;
                        });
                    }

                    // Use getMountDisplayInfo to process mounts
                    if (src.attributes.storage?.mounts) {
                        const processedMounts = getMountDisplayInfo(
                            src.os_type,
                            src.attributes.storage.mounts,
                            false,
                            true,
                            false,
                            false,
                        );
                        processedMounts.forEach((mount) => {
                            mountsCsv += `${mount.mount_name} Size ${mount.size} GiB Free ${mount.free} GiB\n`;
                        });
                    }
                }

                rowCsv.push(formatCSVValue(cpuCsv));

                const memoryGiB = src.attributes.memory
                    ? Math.round((src.attributes.memory.total_kb / 1024 / 1024) * 100) / 100 + ' GiB RAM'
                    : 'N/A';
                rowCsv.push(formatCSVValue(memoryGiB));
                rowCsv.push(formatCSVValue(mountsCsv));

                if (src.recommended_config) {
                    rowCsv.push(formatCSVValue(src.recommended_config.cpu || ''));
                    rowCsv.push(
                        formatCSVValue(src.recommended_config.ram_gb ? src.recommended_config.ram_gb + ' GiB' : ''),
                    );
                } else {
                    if (isVMBased) {
                        // For VM sources without recommended config, add empty columns
                        rowCsv.push(formatCSVValue(''));
                        rowCsv.push(formatCSVValue(''));
                    } else {
                        // For non-VM sources without recommended config, remove the columns from header (only once)
                        if (!headerAdjusted && dataCsv[0].length > 7) {
                            // Remove "Recommended CPU" and "Recommended RAM" columns (indices 6 and 7)
                            dataCsv[0].splice(6, 2);
                            headerAdjusted = true;
                        }
                    }
                }

                // Software (only for non-VM sources)
                if (!isVMBased) {
                    let softwareCsv = '';
                    if (src.attributes.os?.installed_programs && Array.isArray(src.attributes.os.installed_programs)) {
                        src.attributes.os.installed_programs.forEach((software) => {
                            softwareCsv += `${software.display_name || 'N/A'} ${software.display_version || ''}\n`;
                        });
                    }
                    rowCsv.push(formatCSVValue(softwareCsv));
                }

                dataCsv.push(rowCsv);
            });

            csvFileGenerator(dataCsv, 'Source Inspection - Inventory Information');
        } catch (error) {
            console.error('Error generating inventory CSV:', error);
            notifyError({ description: 'Failed to generate CSV file' });
        }
    }, [sessionData.selectedModels, notifySuccess, notifyError]);

    const handleBackToSources = useCallback(() => {
        // Clear session storage
        sessionStorage.removeItem('selectedModels');
        sessionStorage.removeItem('preflight_payload');
        sessionStorage.removeItem('sourceInspectionContext');

        // Navigate back to sources page
        if (window.reactNavigate) {
            window.reactNavigate('/sources');
        } else {
            window.location.href = '/sources';
        }
    }, []);

    // Component interaction handlers
    const handleComponentExpand = useCallback(
        (resourceId) => {
            setExpandedComponents((prev) => {
                const newSet = new Set(prev);
                if (newSet.has(resourceId)) {
                    newSet.delete(resourceId);
                } else {
                    newSet.add(resourceId);
                }
                return newSet;
            });
        },
        [setExpandedComponents],
    );

    const handleRowSelection = useCallback(
        (resourceId, checked) => {
            setSelectedRowKeys((prev) => {
                if (checked) {
                    return [...prev, resourceId];
                } else {
                    return prev.filter((key) => key !== resourceId);
                }
            });
        },
        [setSelectedRowKeys],
    );

    const handleViewAllToggle = useCallback(
        (componentId) => {
            const currentlyHidden = nonErrorsHiddenPerComponent.get(componentId) !== false;
            setNonErrorsHiddenPerComponent((prev) => {
                const newMap = new Map(prev);
                newMap.set(componentId, !currentlyHidden);
                return newMap;
            });
        },
        [nonErrorsHiddenPerComponent, setNonErrorsHiddenPerComponent],
    );

    // Define tab items using modern Tabs API
    const tabItems = [
        {
            key: 'preflight',
            label: 'Preflight Information',
            children: (
                <Card className="preflight-card">
                    {preflightLoading ? (
                        <div className="loading-container">
                            <Spin size="large" />
                            <p>Running preflight checks...</p>
                        </div>
                    ) : preflightError ? (
                        <Alert title="Preflight Error" description={preflightError} type="error" showIcon />
                    ) : preflightComponents.length > 0 ? (
                        <div className="preflight-results">
                            {preflightComponents
                                .filter((component) => component.type !== 'target_cloud') // Only show sources, not target/appliance
                                .map((component, index) => (
                                    <div
                                        key={`${component.resource_id}-${index}`}
                                        className="preflight-component-wrapper"
                                    >
                                        <PreflightComponent
                                            component={component}
                                            inspectSourcesMode={true}
                                            allComplete={allComplete}
                                            expandedComponents={expandedComponents}
                                            selectedRowKeys={selectedRowKeys}
                                            nonErrorsHiddenPerComponent={nonErrorsHiddenPerComponent}
                                            onComponentExpand={handleComponentExpand}
                                            onRowSelection={handleRowSelection}
                                            onViewAllToggle={handleViewAllToggle}
                                            onRecoveryChange={handleErrorWarningRecovery}
                                            getRecoveryState={getRecoveryState}
                                            onRestart={handleRestart}
                                        />
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>No preflight information available</p>
                        </div>
                    )}
                </Card>
            ),
        },
        {
            key: 'inventory',
            label: 'Inventory Information',
            children: (
                <Card className="inventory-card">
                    <SourceInventoryTable sources={sessionData.selectedModels} />
                </Card>
            ),
        },
    ];

    // If no session data, redirect back to sources
    useEffect(() => {
        if (!sessionData.selectedModels.length) {
            console.warn('No source inspection data found in session, redirecting to sources');
            handleBackToSources();
        }
    }, [sessionData.selectedModels, handleBackToSources]);

    return (
        <div className="source-inspection-page">
            <div className="page-header">
                <div className="header-content">
                    <div className="title-section">
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBackToSources}
                            className="back-button"
                        >
                            Back to Sources
                        </Button>
                    </div>

                    <div className="download-buttons">
                        <Space>
                            <Button
                                type="primary"
                                onClick={
                                    activeTab === 'preflight' ? handleDownloadPreflightsPDF : handleDownloadInventoryPDF
                                }
                                disabled={activeTab === 'preflight' ? preflightLoading : false}
                            >
                                Download PDF
                            </Button>
                            <Button
                                type="primary"
                                onClick={
                                    activeTab === 'preflight' ? handleDownloadPreflightsCSV : handleDownloadInventoryCSV
                                }
                                disabled={
                                    activeTab === 'preflight'
                                        ? preflightLoading || !preflightComponents.length
                                        : !sessionData.selectedModels.length
                                }
                            >
                                Download CSV
                            </Button>
                        </Space>
                    </div>
                </div>
            </div>

            <div className="page-content">
                <Tabs activeKey={activeTab} onChange={setActiveTab} className="inspection-tabs" items={tabItems} />
            </div>
        </div>
    );
};

export default SourceInspectionPage;
